// PrivateLayout.js
import React, { useState, useEffect, useCallback } from "react";
import { Box, AppBar, Toolbar, IconButton, useMediaQuery } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Outlet, Navigate } from "react-router-dom";

import MenuNavLeft from "../MenuNavLeft";
import { FEATURES } from "../config/features";
import SidebarMenu from "../components/SidebarMenu";
import { getMenuItems } from "../menuItems";
import NotificationBell from "../components/NotificationBell";
import MessageBell from "../components/MessageBell";
import { useEmails } from "../components/useEmails";

import { useSocket, useSocketState } from "../SocketContext";
import axios from "../axiosConfig";
import NotificationPush from "../components/NotificationPush";
import EmailNotificationPush from "../components/EmailNotificationPush";
import AvatarWithStatus from "../components/AvatarWithStatus";
import SmsBalanceChip from "../components/SmsBalanceChip/SmsBalanceChip";
import { isEndUserRole } from "../utils/userRoles";
import { acmeAppBarSx, acme } from "../theme/acmeTheme";
import { SIDEBAR_WIDTH, AppHeaderBar, BrandLogoMark } from "../components/ui";

const drawerWidth = SIDEBAR_WIDTH;

function PrivateLayout({
  authenticated,
  user,
  setAppUser,
  handleLogout,
  isImpersonating = false,
  onUserChange,
}) {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Состояния для списка заявок и "непрочитанных"
  const [requests, setRequests] = useState([]);
  const [unreadRequests, setUnreadRequests] = useState(new Set());
  // Новое состояние для отслеживания обновленных запросов
  const [updatedRequests, setUpdatedRequests] = useState(new Set());

  const markNotificationAsRead = (notificationId) => {
    axios
      .patch(
        "/api/notifications/read",
        { notificationId },
        { withCredentials: true },
      )
      .then(() => {
        setNotifications((prev) =>
          prev.map((n) =>
            n._id === notificationId ? { ...n, isRead: true } : n,
          ),
        );
      })
      .catch((err) =>
        console.error("Failed to mark notification as read:", err),
      );
  };

  // Эффект для загрузки аккаунтов:
  useEffect(() => {
    // Only attempt to fetch accounts if user is authenticated
    if (authenticated && user?._id) {
      axios
        .get("/api/mail/accounts", { withCredentials: true })
        .then((res) => {
          setAccounts(res.data || []);
          if (res.data && res.data.length > 0) {
            setSelectedAccount(res.data[0]);
          }
        })
        .catch((err) => console.error("Failed to fetch accounts:", err));
    }
  }, [authenticated, user]);

  // Получаем централизованный сокет и его состояние
  const socket = useSocket();
  const { connected } = useSocketState();

  useEffect(() => {
    if (!socket) {
      console.warn(
        "⚠️ No socket available in PrivateLayout, waiting for socket initialization...",
      );
      return;
    }

    if (!connected) {
      console.log(
        "🔄 [PrivateLayout] Socket not connected yet, waiting for connection...",
      );
      return;
    }

    console.log("✅ [PrivateLayout] Socket connected:", socket.id);

    socket.on("notificationCreated", (notification) => {
      console.log(
        "✅ [PrivateLayout] notificationCreated event received:",
        notification,
      );

      // Просто логируем событие, обработка теперь происходит в EmailNotificationPush
      if (notification.type === "newEmail") {
        console.log(
          "✅ [PrivateLayout] Email notification received (handled by EmailNotificationPush)",
        );
      } else {
        console.log(
          "✅ [PrivateLayout] Regular notification received (handled by NotificationPush)",
        );
      }
    });

    return () => {
      socket.off("notificationCreated");
    };
  }, [socket, connected]);

  const { emailMessages, markEmailAsRead } = useEmails(
    authenticated,
    user,
    selectedAccount,
  );

  // Обновляем число непрочитанных заявок
  useEffect(() => {
    if (user?._id) {
      const userIdStr = String(user._id);

      // Раздельные массивы для непрочитанных и обновленных запросов
      const notViewed = [];
      const updated = [];

      requests.forEach((r) => {
        if (r.status === "Closed") return; // Игнорируем закрытые запросы

        // Проверяем наличие записи о просмотре для текущего пользователя
        const viewRecord =
          Array.isArray(r.viewedBy) &&
          r.viewedBy.find((v) => v.userId && String(v.userId) === userIdStr);

        if (!viewRecord) {
          // Запрос вообще не просмотрен пользователем
          notViewed.push(r._id);
          return;
        }

        // Проверяем, было ли обновление запроса после последнего просмотра
        if (r.lastUpdatedAt && viewRecord.lastViewedAt) {
          const lastUpdated = new Date(r.lastUpdatedAt);
          const lastViewed = new Date(viewRecord.lastViewedAt);

          // Если lastUpdatedBy - это текущий пользователь, игнорируем
          if (r.lastUpdatedBy && String(r.lastUpdatedBy) === userIdStr) {
            return;
          }

          // Если запрос обновлен после последнего просмотра
          if (lastUpdated > lastViewed) {
            updated.push(r._id);
          }
        }
      });

      console.log(
        "Unread requests count:",
        notViewed.length,
        "Updated requests count:",
        updated.length,
      );
      setUnreadRequests(new Set(notViewed));
      setUpdatedRequests(new Set(updated));
    }
  }, [requests, user]);

  // Загружаем уведомления при изменении пользователя или аутентификации
  useEffect(() => {
    if (authenticated && user?._id) {
      axios
        .get("/api/notifications", { withCredentials: true })
        .then((res) => {
          setNotifications(res.data || []);
        })
        .catch((err) => console.error("Failed to fetch notifications:", err));
    }
  }, [authenticated, user]);

  // Подписываемся на событие "notificationCreated" для уведомлений
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (newNotif) => {
      console.log(
        "🔄 [PrivateLayout] Updating notifications list with new notification:",
        newNotif,
      );

      // Событие обрабатываем, если оно адресовано этому пользователю И это не email-уведомление
      if (
        newNotif.user &&
        user?._id &&
        newNotif.user.toString() === user._id.toString() &&
        newNotif.type !== "newEmail" // Игнорируем email-уведомления, они обрабатываются в EmailNotificationPush
      ) {
        console.log("✅ [PrivateLayout] Adding regular notification to list");
        // Добавляем уведомление в список только для отображения в панели уведомлений
        // Всплывающие уведомления обрабатываются в NotificationPush
        setNotifications((prev) => [newNotif, ...prev]);
      } else if (newNotif.type === "newEmail") {
        console.log(
          "ℹ️ [PrivateLayout] Ignoring email notification in notifications list",
        );
      }
    };

    socket.on("notificationCreated", handleNotification);
    return () => {
      socket.off("notificationCreated", handleNotification);
    };
  }, [socket, user]);

  // Подписка на событие "requestUpdated"
  useEffect(() => {
    if (!FEATURES.PROJECTS_ENABLED || !socket) return;
    const handleRequestUpdated = (updatedReq) => {
      setRequests((prevRequests) => {
        const index = prevRequests.findIndex((r) => r._id === updatedReq._id);
        if (index === -1) {
          return [...prevRequests, updatedReq];
        } else {
          return prevRequests.map((r) =>
            r._id === updatedReq._id ? updatedReq : r,
          );
        }
      });
    };

    socket.on("requestUpdated", handleRequestUpdated);
    return () => {
      socket.off("requestUpdated", handleRequestUpdated);
    };
  }, [socket]);

  // Загружаем список заявок при изменении пользователя
  useEffect(() => {
    if (!FEATURES.PROJECTS_ENABLED || !authenticated || !user?._id) return;

    axios
      .get("/api/requests", { withCredentials: true })
      .then((res) => {
        const data = res.data || [];
        setRequests(data);

        const userIdStr = String(user._id);
        const notViewed = [];
        const updated = [];

        data.forEach((r) => {
          if (r.status === "Closed") return; // Игнорируем закрытые запросы

          // Проверяем наличие записи о просмотре для текущего пользователя
          const viewRecord =
            Array.isArray(r.viewedBy) &&
            r.viewedBy.find((v) => v.userId && String(v.userId) === userIdStr);

          if (!viewRecord) {
            // Запрос вообще не просмотрен пользователем
            notViewed.push(r._id);
            return;
          }

          // Проверяем, было ли обновление запроса после последнего просмотра
          if (r.lastUpdatedAt && viewRecord.lastViewedAt) {
            const lastUpdated = new Date(r.lastUpdatedAt);
            const lastViewed = new Date(viewRecord.lastViewedAt);

            // Если lastUpdatedBy - это текущий пользователь, игнорируем
            if (r.lastUpdatedBy && String(r.lastUpdatedBy) === userIdStr) {
              return;
            }

            // Если запрос обновлен после последнего просмотра
            if (lastUpdated > lastViewed) {
              updated.push(r._id);
            }
          }
        });

        console.log(
          "Initial unread requests count:",
          notViewed.length,
          "Initial updated requests count:",
          updated.length,
        );
        setUnreadRequests(new Set(notViewed));
        setUpdatedRequests(new Set(updated));
      })
      .catch((err) => {
        console.error("Failed to fetch requests:", err);
      });
  }, [authenticated, user]);

  // Единый источник user — проп из App; патчим через setAppUser, чтобы дочерние страницы сразу видели изменения
  const updateUser = useCallback(
    (newUserData) => {
      if (typeof setAppUser !== "function") return;
      setAppUser((prevUser) =>
        prevUser ? { ...prevUser, ...newUserData } : prevUser,
      );
    },
    [setAppUser],
  );

  // Открыть/закрыть мобильное меню
  const toggleDrawer = (open) => () => {
    setMobileOpen(open);
  };

  // Формируем пункты меню
  const menuItems = getMenuItems(authenticated, user);
  const headerItems = menuItems.filter(
    (item) => item.text === "Home" || item.text === "About Us",
  );

  // Кнопка "Pricing" — в AppHeaderBar
  const headerRightSlot = authenticated ? (
    <>
      <NotificationBell
        key="notification-bell"
        notifications={notifications}
        onMarkAsRead={markNotificationAsRead}
        user={user}
        handleLogout={handleLogout}
        iconColor={acme.colors.iconDefault}
      />
      {!isEndUserRole(user?.role) && (
        <MessageBell
          key="message-bell"
          messages={emailMessages}
          onMarkAsRead={markEmailAsRead}
          iconColor={acme.colors.iconDefault}
        />
      )}
      <SmsBalanceChip key="sms-balance" />
      <IconButton
        key="avatar-menu"
        sx={{ padding: 0, marginLeft: 0.5 }}
        onClick={() => {
          const profileMenuButton = document.getElementById(
            "profile-menu-button",
          );
          if (profileMenuButton) profileMenuButton.click();
        }}
      >
        <AvatarWithStatus user={user} sx={{ width: 36, height: 36 }} />
      </IconButton>
    </>
  ) : null;

  // Считаем число непрочитанных заявок
  const unreadCount = unreadRequests.size;
  // Считаем число обновленных заявок
  const updatedCount = updatedRequests.size;

  return authenticated ? (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Компоненты обработки уведомлений вне основной структуры, чтобы не нарушать макет */}
      {user && <NotificationPush userId={user._id} />}
      {user && <EmailNotificationPush userId={user._id} />}

      {/* Левое меню */}
      {isMobile ? (
        <SidebarMenu
          menuItems={menuItems}
          mobileOpen={mobileOpen}
          toggleDrawer={toggleDrawer}
          isAuthenticated={authenticated}
          handleLogout={handleLogout}
        />
      ) : (
        <MenuNavLeft
          user={user}
          unreadCount={unreadCount}
          updatedCount={updatedCount}
          handleLogout={handleLogout}
        />
      )}

      {/* Верхняя панель (AppBar) */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          ...(isMobile
            ? acmeAppBarSx(isMobile)
            : {
                backgroundColor: "transparent",
                color: acme.colors.foreground,
                boxShadow: "none",
                borderBottom: "none",
              }),
          marginLeft: isMobile ? 0 : drawerWidth,
          width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
          top: isImpersonating ? 48 : 0,
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 64, sm: 72 },
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 0.5, sm: 1 },
            ...(isMobile ? {} : { backgroundColor: "transparent" }),
          }}
        >
          {isMobile ? (
            <>
              <Box
                sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}
              >
                <BrandLogoMark to="/dashboard" />
              </Box>
              <IconButton
                sx={{ color: acme.colors.background }}
                onClick={toggleDrawer(true)}
              >
                <MenuIcon />
              </IconButton>
            </>
          ) : (
            <AppHeaderBar links={headerItems} rightSlot={headerRightSlot} />
          )}
        </Toolbar>
      </AppBar>

      {/* Основной контент */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          marginLeft: isMobile ? 0 : 0, // Убираем отступ для контента
          marginTop: isImpersonating ? "48px" : "0", // Добавляем отступ сверху если активен режим импersonation
        }}
      >
        <Toolbar />
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3 },
            backgroundColor: acme.colors.pageBg,
            minHeight: "calc(100vh - 64px)",
          }}
        >
          <Outlet
            context={{
              authenticated,
              user,
              handleLogout,
              updateUser,
              onUserChange,
              requests,
              setRequests,
              unreadRequests,
              setUnreadRequests,
              updatedRequests,
              setUpdatedRequests,
              notifications,
              markNotificationAsRead,
            }}
          />
        </Box>
      </Box>
    </Box>
  ) : (
    <Navigate to="/login" replace />
  );
}

export default PrivateLayout;
